import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";

interface Product {
  id: string;
  category: string;
  name: string;
  material: string;
  price: string;
  description: string;
  image: string;
  marca: string;
}

export const HomePage = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  const SPREADSHEET_ID = "1P6aNYWLOmysvMxCGomJX3sHD630bIEIbRdrVK0kj42g";
  const YOUR_API_KEY = "AIzaSyDApd2IAji0C8D7cTpv8phMYiqDShinc5c";
  const SHEET_NAME = "Strippoli"; // Nome del foglio

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setErrorMessage(null);
        console.log("Inizio caricamento prodotti da Google Sheets...");

        const url = `https://sheets.googleapis.com/v4/spreadsheets/${SPREADSHEET_ID}/values/${SHEET_NAME}?key=${YOUR_API_KEY}`;
        console.log("URL della richiesta:", url);

        const response = await axios.get(url);
        const rows = response.data.values;

        console.log("Dati ricevuti:", rows);

        if (!rows || rows.length <= 1) {
          throw new Error("Nessun dato trovato nel foglio.");
        }

        const loadedProducts = rows.slice(1).map((row: string[]) => ({
          id: row[0],
          category: row[1],
          name: row[2],
          material: row[3],
          price: row[4],
          description: row[5],
          image: row[6]?.replace("/view?usp=drive_link", "/preview"),
          marca: row[7],
        }));

        setProducts(loadedProducts);
        console.log("Prodotti caricati:", loadedProducts);
      } catch (err: any) {
        let errorDetail = "Errore sconosciuto.";
        if (err.response) {
          errorDetail = `Errore API: ${err.response.data.error.message}`;
        } else if (err.request) {
          errorDetail = "Impossibile connettersi all'API di Google Sheets.";
        } else {
          errorDetail = err.message || "Errore imprevisto.";
        }
        setErrorMessage(errorDetail);
        console.error("Errore durante il recupero dei dati:", errorDetail);
      }
    };

    fetchProducts();
  }, []);

  return (
    <>
      <Navbar />

      <main className="flex flex-col items-center bg-gradient-to-b from-gray-50 to-white w-full font-elegant">
        {/* Hero Section */}
        <section className="w-full relative">
          <div className="w-full h-[200px] sm:h-[250px] md:h-[400px] lg:h-[500px] relative overflow-hidden">
            <img
              className="w-full h-full object-cover"
              src="/img/Sfondoiniziale.png"
              alt="Vetrina orologeria e gioielleria Strippoli Monza"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8 lg:p-12 text-white">
              <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold mb-1 sm:mb-2 drop-shadow-lg leading-tight">
                Orologeria & Gioielleria Strippoli
              </h1>
              <p className="text-xs sm:text-sm md:text-lg lg:text-xl opacity-90 drop-shadow-md">
                Dal dopoguerra, tradizione e passione a Monza
              </p>
            </div>
          </div>
        </section>

        {/* Sezione Gioielleria e Orologeria */}
        <section className="w-full max-w-7xl mt-6 sm:mt-8 md:mt-12 px-3 sm:px-4 md:px-6">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-4 sm:mb-6 md:mb-10 text-gray-800">
            Le Nostre Collezioni
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
            {/* Gioielleria Card */}
            <a
              href="/gioielleria"
              className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 focus:outline-none focus:ring-4 focus:ring-green-800"
              aria-label="Vai alla sezione Gioielleria"
            >
              <div className="aspect-[4/5] relative">
                <img
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  src="/img/Milunaimg2.png"
                  alt="Collezione gioielleria"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2">
                    GIOIELLERIA
                  </h3>
                  <p className="text-xs sm:text-sm md:text-base text-white/90 mb-3 sm:mb-4">
                    Scopri le nostre eleganti collezioni di gioielli
                  </p>
                  <span className="inline-flex items-center text-white text-sm sm:text-base font-medium group-hover:gap-3 gap-2 transition-all">
                    Esplora
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </div>
            </a>

            {/* Orologeria Card */}
            <a
              href="/orologeria"
              className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 focus:outline-none focus:ring-4 focus:ring-green-800"
              aria-label="Vai alla sezione Orologeria"
            >
              <div className="aspect-[4/5] relative">
                <img
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  src="/img/Lorenzimg2.png"
                  alt="Collezione orologeria"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2">
                    OROLOGERIA
                  </h3>
                  <p className="text-xs sm:text-sm md:text-base text-white/90 mb-3 sm:mb-4">
                    Orologi di prestigio per ogni occasione
                  </p>
                  <span className="inline-flex items-center text-white text-sm sm:text-base font-medium group-hover:gap-3 gap-2 transition-all">
                    Esplora
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </div>
            </a>
          </div>
        </section>

        {/* Laboratorio */}
        <section className="w-full max-w-7xl mt-8 sm:mt-12 md:mt-20 px-3 sm:px-4 md:px-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              <div className="relative h-[200px] sm:h-[250px] md:h-[350px] lg:h-auto">
                <img
                  className="w-full h-full object-cover"
                  src="/img/rectangle-5-2@2x.png"
                  alt="Laboratorio di orologeria Strippoli"
                />
              </div>
              <div className="p-4 sm:p-6 md:p-10 lg:p-12 flex flex-col justify-center bg-gradient-to-br from-gray-50 to-white">
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">
                  Laboratorio di Orologeria
                </h2>
                <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed">
                  Oggi l'orologeria/oreficeria Strippoli è una realtà conosciuta e ben
                  radicata nel territorio monzese ed è guidata da Maurizio e Laura,
                  nipoti del fondatore e animati dallo stesso spirito. Maurizio ha
                  affinato le proprie capacità tecniche nella scuola di orologeria
                  Capac di Milano, mentre Laura, addetta alle vendite ha la capacità
                  innata di mettere a proprio agio il cliente e indirizzandolo nella
                  scelta giusta.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Prodotti in evidenza */}
        <section className="w-full bg-gray-50 py-8 sm:py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6">
            <div className="text-center mb-6 sm:mb-8 md:mb-12">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-2 sm:mb-3">
                Prodotti in Evidenza
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-600">
                Scopri una selezione dei nostri migliori articoli
              </p>
            </div>

            {errorMessage ? (
              <p className="text-red-500 text-center" role="alert">{errorMessage}</p>
            ) : (
              <>
                {/* Grid responsive: 2 colonne mobile, 3 tablet, 4 desktop */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-4 md:gap-6">
                  {products.slice(0, 6).map((product) => (
                    <button
                      key={product.id}
                      className="group w-full min-h-[44px] focus:outline-none focus:ring-4 focus:ring-green-800 rounded-lg sm:rounded-xl"
                      onClick={() => navigate("/prodotti-orologeria", { state: product })}
                      aria-label={`Vedi dettagli prodotto ${product.name}, ${product.material}, prezzo ${product.price}`}
                    >
                      <div className="bg-white rounded-lg sm:rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden transform group-hover:-translate-y-2 h-full">
                        <div className="aspect-square relative overflow-hidden bg-gray-100">
                          <img
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            src={product.image}
                            alt={`${product.name} - ${product.material}`}
                          />
                        </div>
                        <div className="p-2 sm:p-3 md:p-4">
                          <h3 className="text-xs sm:text-sm md:text-base lg:text-lg font-bold text-gray-800 mb-1 line-clamp-2 text-left">
                            {product.name}
                          </h3>
                          <p className="text-[10px] sm:text-xs md:text-sm text-gray-500 mb-1.5 sm:mb-2 text-left line-clamp-1">
                            {product.material}
                          </p>
                          <p className="text-sm sm:text-base md:text-lg font-bold text-green-800 text-left">
                            {product.price}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Pulsante "Vedi tutti" */}
                {products.length > 6 && (
                  <div className="text-center mt-6 sm:mt-8 md:mt-10">
                    <a
                      href="/orologeria"
                      className="inline-flex items-center gap-2 bg-green-800 hover:bg-green-900 text-white font-semibold py-3 px-6 sm:px-8 rounded-lg sm:rounded-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-green-800 min-h-[44px]"
                      aria-label="Vedi tutti i prodotti"
                    >
                      <span className="text-sm sm:text-base">Vedi Tutti i Prodotti</span>
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};
